import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import useCountUp from "@/hooks/useCountUp";
import { Search, Plus, Loader2, Tag as TagIcon, Users, Pencil, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const inputCls =
  "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const labelCls = "text-xs tracking-[0.2em] uppercase font-bold text-slate-500";
const primaryBtn =
  "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn =
  "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

const CATEGORIES = [
  { value: "custom", label: "Custom" },
  { value: "campaign", label: "Campaign" },
  { value: "source", label: "Source" },
  { value: "lead_stage", label: "Lead Stage" },
  { value: "customer_type", label: "Customer Type" },
  { value: "engagement", label: "Engagement" },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const COLOR_SWATCHES = [
  "#DB2777", "#EA580C", "#D97706", "#65A30D", "#059669",
  "#0891B2", "#2563EB", "#7C3AED", "#DC2626", "#475569",
];

function TagPill({ name, color }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ background: `${color}22`, color }}
    >
      {name}
    </span>
  );
}

export default function Tags() {
  const { activeBranchId } = useBranch();
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null); // null = closed, {} = create, {...tag} = edit
  const [form, setForm] = useState({ name: "", color: COLOR_SWATCHES[0], category: "custom" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/tags", { params: { branch_id: activeBranchId } });
      setTags(data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => {
    load();
  }, [load]);

  const usedCategories = useMemo(
    () => CATEGORIES.filter((c) => tags.some((t) => (t.category || "custom") === c.value)),
    [tags]
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return tags.filter((t) => {
      const matchesSearch = !s || t.name.toLowerCase().includes(s);
      const matchesCategory = category === "all" || (t.category || "custom") === category;
      return matchesSearch && matchesCategory;
    });
  }, [tags, search, category]);

  const totalTagged = useMemo(
    () => tags.reduce((sum, t) => sum + (t.contact_count || 0), 0),
    [tags]
  );
  const totalTagsDisplay = useCountUp(tags.length);
  const totalTaggedDisplay = useCountUp(totalTagged);

  const openCreate = () => {
    setForm({ name: "", color: COLOR_SWATCHES[0], category: "custom" });
    setEditing({});
  };

  const openEdit = (tag) => {
    setForm({ name: tag.name, color: tag.color, category: tag.category || "custom" });
    setEditing(tag);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/tags/${editing.id}`, form);
        toast.success("Tag updated");
      } else {
        await api.post("/tags", { branch_id: activeBranchId, ...form });
        toast.success("Tag created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tags/${deleteTarget.id}`);
      toast.success("Tag deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="tags-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Tags</h1>
          <p className="text-sm text-slate-500 mt-2">
            Label contacts to track sources, campaigns, and more.
          </p>
        </div>
        <button data-testid="create-tag-button" className={primaryBtn} onClick={openCreate}>
          <Plus className="h-4 w-4" /> Create Tag
        </button>
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
        className="grid grid-cols-2 gap-4 mb-6"
      >
        <motion.div
          variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } } }}
          className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30"
        >
          <div className="flex items-center justify-between">
            <p className={labelCls}>Total Tags</p>
            <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
              <TagIcon className="h-4 w-4 text-orange-600" />
            </div>
          </div>
          <p className="font-heading text-2xl font-black text-white mt-2">{totalTagsDisplay}</p>
        </motion.div>
        <motion.div
          variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } } }}
          className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30"
        >
          <div className="flex items-center justify-between">
            <p className={labelCls}>Tagged Contacts</p>
            <div className="h-8 w-8 rounded-md bg-orange-500/10 flex items-center justify-center">
              <Users className="h-4 w-4 text-orange-600" />
            </div>
          </div>
          <p className="font-heading text-2xl font-black text-white mt-2">{totalTaggedDisplay}</p>
        </motion.div>
      </motion.div>

      <div className="relative max-w-sm mb-4">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="tags-search-input"
          className={`${inputCls} pl-9`}
          placeholder="Search tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button
          data-testid="tag-category-all"
          onClick={() => setCategory("all")}
          className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-all duration-150 ${
            category === "all" ? "border-orange-500 bg-orange-500/10 text-white" : "border-white/15 text-slate-400 hover:text-white"
          }`}
        >
          All
        </button>
        {usedCategories.map((c) => (
          <button
            key={c.value}
            data-testid={`tag-category-${c.value}`}
            onClick={() => setCategory(c.value)}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-all duration-150 ${
              category === c.value ? "border-orange-500 bg-orange-500/10 text-white" : "border-white/15 text-slate-400 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden bg-[#141414]">
        <Table>
          <TableHeader>
            <TableRow className="bg-white/5">
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Tag</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Category</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500">Contacts</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-slate-400 text-sm">
                  No tags yet. Create one, or add tags while importing contacts.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.id} data-testid={`tag-row-${t.id}`}>
                  <TableCell>
                    <TagPill name={t.name} color={t.color} />
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {CATEGORY_LABEL[t.category] || t.category}
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-slate-500" /> {t.contact_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`edit-tag-${t.id}`}
                      onClick={() => openEdit(t)}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-white/10 hover:text-white transition-all duration-150"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`delete-tag-${t.id}`}
                      onClick={() => setDeleteTarget(t)}
                      className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10 transition-all duration-150"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit tag dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="tag-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing?.id ? "Edit Tag" : "Create Tag"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Name</label>
              <input
                data-testid="tag-name-input"
                className={`mt-1.5 ${inputCls}`}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. diwali"
                autoFocus
              />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select
                data-testid="tag-category-select"
                className={`mt-1.5 ${inputCls}`}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Color</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {COLOR_SWATCHES.map((c) => (
                  <button
                    key={c}
                    data-testid={`tag-color-${c}`}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`h-7 w-7 rounded-full transition-all duration-150 ${
                      form.color === c ? "ring-2 ring-offset-2 ring-offset-[#141414] ring-white" : ""
                    }`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              <div className="mt-3">
                <TagPill name={form.name || "Preview"} color={form.color} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button
              data-testid="save-tag-button"
              className={primaryBtn}
              onClick={save}
              disabled={saving || !form.name.trim()}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-tag-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the tag from all {deleteTarget?.contact_count || 0} contact(s) that have it. The contacts themselves are not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-tag"
              className="bg-red-600 hover:bg-red-700"
              onClick={doDelete}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
